import { lazy, Suspense } from 'react'
import { useParams } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Skeleton } from '@/components/ui/Skeleton'
import { Button } from '@/components/ui/Button'
import { formatDateTime } from '@/lib/format'
import { useCaseDetail } from './hooks/useCaseDetail'
import { LinkedAlertsTable } from './components/LinkedAlertsTable'
import { InvestigationHeader } from './components/InvestigationHeader'
import { CustomerSidebarCard } from './components/CustomerSidebarCard'
import { InvestigationActionsChecklist } from './components/InvestigationActionsChecklist'
import { FraudSequenceTimeline } from './components/FraudSequenceTimeline'
import { AICopilotSummaryCard } from './components/AICopilotSummaryCard'
import { KeyEvidenceCards } from './components/KeyEvidenceCards'
import { DispositionPanel } from './components/DispositionPanel'
import { CaseNotesPanel } from './components/CaseNotesPanel'
import { AffectedAssetsCard } from './components/AffectedAssetsCard'
import { CaseDetectionSummary } from './components/CaseDetectionSummary'

// AF-243 — lazy-load the POS block so Leaflet (~150 KB) and @xyflow/react
// (~50 KB) only ship to analysts who actually open a POS-flavoured case.
const PosCaseBlock = lazy(() =>
  import('./components/pos/PosCaseBlock').then((m) => ({ default: m.PosCaseBlock })),
)

// AF-244 — lazy-load the USSD block too. Smaller than the POS block but
// the menu-path replay isn't useful outside USSD-flavoured cases.
const UssdCaseBlock = lazy(() =>
  import('./components/ussd/UssdCaseBlock').then((m) => ({
    default: m.UssdCaseBlock,
  })),
)

// AF-245 — lazy-load the mobile-device block.
const MobileCaseBlock = lazy(() =>
  import('./components/mobile/MobileCaseBlock').then((m) => ({
    default: m.MobileCaseBlock,
  })),
)

// AF-246 — lazy-load the internet-banking web session block.
const WebCaseBlock = lazy(() =>
  import('./components/web/WebCaseBlock').then((m) => ({
    default: m.WebCaseBlock,
  })),
)

// AF-247 — lazy-load the agent-banking block.
const AgentCaseBlock = lazy(() =>
  import('./components/agent/AgentCaseBlock').then((m) => ({
    default: m.AgentCaseBlock,
  })),
)

/**
 * AF-127 — case detail page (catalogue-aligned layout).
 *
 * Two-column investigation workspace:
 *   - left  (260px): Customer card with risk-score gauge +
 *                    Investigation Actions checklist with progress
 *   - right (flex):  Investigation header (action buttons),
 *                    Fraud Sequence Timeline,
 *                    AI Copilot Summary,
 *                    3-card Key Evidence panel,
 *                    per-channel context blocks (POS / USSD / Mobile /
 *                    Web / Agent — each lazy-loaded),
 *                    Linked Alerts table,
 *                    Case Timeline.
 *
 * The visual structure mirrors the "Investigation Workspace / Case
 * F-2026-8891" mock from the product catalogue. Existing per-channel
 * blocks remain as-is — the catalogue's evidence cards summarise
 * cross-channel signals and the per-channel blocks are the deep dives.
 */
export function CaseDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { data: c, isLoading, isError, error, refetch } = useCaseDetail(id)

  if (isLoading) return <DetailSkeleton />
  if (isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Couldn't load case</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <p className="text-sm text-muted-fg">{error.message}</p>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            Retry
          </Button>
        </CardContent>
      </Card>
    )
  }
  if (!c) return null

  return (
    <article className="flex flex-col gap-4">
      <InvestigationHeader c={c} />

      {c.title || c.summary ? (
        <Card>
          <CardContent className="space-y-1 py-3">
            <h2 className="text-base font-semibold">{c.title}</h2>
            {c.summary ? (
              <p className="max-w-prose text-sm text-muted-fg">{c.summary}</p>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[16rem_1fr]">
        {/* Left rail: customer card + investigation checklist */}
        <aside className="flex flex-col gap-3">
          <CustomerSidebarCard c={c} />
          <InvestigationActionsChecklist c={c} />
        </aside>

        {/* Main column: timeline → AI summary → evidence → channel
            blocks → linked alerts → case-level timeline. */}
        <div className="flex flex-col gap-4">
          <AffectedAssetsCard c={c} />
          <CaseDetectionSummary c={c} />
          <FraudSequenceTimeline alerts={c.linked_alerts} />
          <AICopilotSummaryCard c={c} />
          <DispositionPanel c={c} />
          <CaseNotesPanel caseId={c.id} />
          <KeyEvidenceCards c={c} />

          {c.terminal_context && (
            <Suspense fallback={<Skeleton className="h-96 w-full" />}>
              <PosCaseBlock caseId={c.id} terminal={c.terminal_context} />
            </Suspense>
          )}
          {c.ussd_context && (
            <Suspense fallback={<Skeleton className="h-72 w-full" />}>
              <UssdCaseBlock caseId={c.id} context={c.ussd_context} />
            </Suspense>
          )}
          {c.mobile_context && (
            <Suspense fallback={<Skeleton className="h-72 w-full" />}>
              <MobileCaseBlock caseId={c.id} context={c.mobile_context} />
            </Suspense>
          )}
          {c.web_context && (
            <Suspense fallback={<Skeleton className="h-72 w-full" />}>
              <WebCaseBlock caseId={c.id} context={c.web_context} />
            </Suspense>
          )}
          {c.agent_context && (
            <Suspense fallback={<Skeleton className="h-72 w-full" />}>
              <AgentCaseBlock caseId={c.id} context={c.agent_context} />
            </Suspense>
          )}

          <LinkedAlertsTable caseId={c.id} alerts={c.linked_alerts} />

          <Card>
            <CardHeader>
              <CardTitle>Case timeline</CardTitle>
            </CardHeader>
            <CardContent>
              {c.timeline.length === 0 ? (
                <p className="text-sm text-muted-fg">No case-level events yet.</p>
              ) : (
                <ol className="relative flex flex-col gap-3 border-l border-border pl-4">
                  {c.timeline.map((entry) => (
                    <li key={entry.id} className="relative">
                      <span
                        aria-hidden
                        className="absolute -left-[1.32rem] top-1.5 h-2 w-2 rounded-full bg-primary ring-4 ring-card"
                      />
                      <p className="text-sm">{entry.message}</p>
                      <p className="mt-0.5 text-xs text-muted-fg">
                        {entry.by ? `${entry.by.display_name} · ` : ''}
                        {formatDateTime(entry.at)}
                      </p>
                    </li>
                  ))}
                </ol>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </article>
  )
}

function DetailSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      <Skeleton className="h-16 w-full" />
      <div className="grid gap-4 lg:grid-cols-[16rem_1fr]">
        <Skeleton className="h-96 w-full" />
        <div className="flex flex-col gap-3">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    </div>
  )
}
